import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Flex, Grid } from '@decisiv/ui-components';
import debounce from 'lodash/debounce';
import ChangeHistory from '../../components/ChangeHistory';
import IntegrationsApi from '../../api/integrations';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';
import ChangeHistoryWrapper from './ChangeHistoryWrapper.styled';
import localCache from '../../utils/localCache';
import { changeHistorySettingsKey } from '../../utils/storageKeys';

const DEFAULT_PAGE_SIZE = 10;

function readSavedSettings(key) {
  return localCache.get(key, {}) || {};
}

export default function ChangeHistoryContainer({
  integration,
  location = null,
  title = undefined,
}) {
  const lockedLocation = location?.id ? location : null;

  const storageKey = changeHistorySettingsKey(
    integration?.id,
    lockedLocation?.id,
  );

  const [mode, setMode] = useState(() =>
    lockedLocation
      ? 'location'
      : readSavedSettings(storageKey).mode || 'global',
  );
  const [selectedLocation, setSelectedLocation] = useState(
    () =>
      lockedLocation || readSavedSettings(storageKey).selectedLocation || null,
  );
  const [comboInput, setComboInput] = useState(() =>
    lockedLocation ? '' : readSavedSettings(storageKey).comboInput || '',
  );
  const [comboOptions, setComboOptions] = useState([]);
  const [comboLoading, setComboLoading] = useState(false);

  const [allHistory, setAllHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => readSavedSettings(storageKey).pageSize || DEFAULT_PAGE_SIZE,
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!lockedLocation) return;
    setMode('location');
    setSelectedLocation((prev) =>
      prev?.id === lockedLocation.id ? prev : lockedLocation,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedLocation?.id]);

  useEffect(() => {
    if (!storageKey) return;
    localCache.set(
      storageKey,
      lockedLocation
        ? { pageSize }
        : { mode, pageSize, selectedLocation, comboInput },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storageKey,
    lockedLocation?.id,
    mode,
    pageSize,
    selectedLocation,
    comboInput,
  ]);

  const searchLocations = useMemo(
    () =>
      debounce(async (integrationId, term) => {
        if (!term || term.length < 2) {
          setComboOptions([]);
          setComboLoading(false);
          return;
        }
        setComboLoading(true);
        const response =
          await ConfiguredIntegrationsApi.searchAllConfiguredIntegrationsByLocationSearchTerm(
            integrationId,
            term,
          );
        if (response.data) {
          setComboOptions(
            response.data.map((loc) => {
              const isRemoved = !!loc.attributes.discarded_at;
              const suffix = isRemoved ? ' · Removed' : '';
              return {
                id: loc.id,
                label: `${loc.attributes.name} · SRM #${loc.attributes.srm_account_id}${suffix}`,
                value: loc,
              };
            }),
          );
        }
        setComboLoading(false);
      }, 300),
    [],
  );

  const handleComboInputChange = useCallback(
    (e) => {
      const term = e ? e.target.value : '';
      setComboInput(term);
      if (!term) {
        setComboOptions([]);
        setSelectedLocation(null);
      }
      searchLocations(integration?.id, term);
    },
    [integration?.id, searchLocations],
  );

  const handleComboValueChange = useCallback(
    (selectedId) => {
      const option = comboOptions.find((o) => o.id === selectedId);
      if (!option) return;
      setSelectedLocation(option.value);
      setComboInput(option.label);
      setAllHistory([]);
      setPage(1);
      setTotalRecords(0);
    },
    [comboOptions],
  );

  const handleModeChange = useCallback((next) => {
    setMode(next);
    setSelectedLocation(null);
    setComboInput('');
    setComboOptions([]);
    setAllHistory([]);
    setPage(1);
    setTotalRecords(0);
  }, []);

  const isReady =
    mode === 'global' || (mode === 'location' && !!selectedLocation);

  useEffect(() => {
    if (!integration?.id || !isReady) {
      setLoading(false);
      return;
    }

    async function fetchHistory() {
      setLoading(true);
      let response;
      if (mode === 'global') {
        response = await IntegrationsApi.fetchIntegrationChangeHistory(
          integration.id,
          1,
          pageSize,
        );
      } else {
        response =
          await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsChangeHistory(
            selectedLocation.id,
            1,
            pageSize,
          );
      }
      if (response.data) {
        setAllHistory(Object.values(response.data));
        setTotalRecords(response.totalRecords ?? 0);
        setPage(1);
      }
      setLoading(false);
    }

    fetchHistory();
  }, [integration?.id, mode, selectedLocation?.id, pageSize, isReady]);

  // When totalRecords is 0 the backend didn't return meta — assume there may
  // be more until a fetch comes back empty and we set totalRecords to the
  // current length (marking exhaustion).
  const hasMore = totalRecords === 0 || allHistory.length < totalRecords;

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    const nextPage = page + 1;
    let response;
    if (mode === 'global') {
      response = await IntegrationsApi.fetchIntegrationChangeHistory(
        integration?.id,
        nextPage,
        pageSize,
      );
    } else {
      response =
        await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsChangeHistory(
          selectedLocation?.id,
          nextPage,
          pageSize,
        );
    }
    if (response.data) {
      const newEntries = Object.values(response.data);
      if (newEntries.length > 0) {
        setAllHistory((prev) => [...prev, ...newEntries]);
        setPage(nextPage);
      } else {
        // Backend confirmed no more pages — mark as exhausted
        setTotalRecords((prev) => prev || allHistory.length);
      }
    }
    setLoadingMore(false);
  }, [
    integration?.id,
    mode,
    selectedLocation?.id,
    page,
    pageSize,
    allHistory.length,
  ]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
  }, []);

  return (
    <ChangeHistoryWrapper>
      <Flex flex={1}>
        <Grid.Container>
          <ChangeHistory
            histories={allHistory}
            integration={integration}
            location={selectedLocation}
            locked={!!lockedLocation}
            title={title}
            loading={loading}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            mode={mode}
            onModeChange={handleModeChange}
            comboInput={comboInput}
            comboOptions={comboOptions}
            comboLoading={comboLoading}
            onComboInputChange={handleComboInputChange}
            onComboValueChange={handleComboValueChange}
          />
        </Grid.Container>
      </Flex>
    </ChangeHistoryWrapper>
  );
}
ChangeHistoryContainer.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  location: PropTypes.oneOfType([PropTypes.object]),
  title: PropTypes.string,
};
